import CompactDoc from './CompactDoc';
import {
    meetingInsightsSectionAriaLabel,
    showAllInsightsAriaLabel,
} from './Insights.locstring.json';
import getInsightDocRowIcon from './getInsightDocRowIcon';
import {
    EmailInsightType,
    FileInsightType,
    InsightsDataFetchSource,
} from 'owa-meeting-insights-types-and-flights';
import { ActionButton, IButtonStyles } from '@fluentui/react/lib/Button';
import { observer } from 'mobx-react-lite';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import loc from 'owa-localize';
import { getPalette } from 'owa-theme';
import * as React from 'react';

import styles from './InsightDocRow.scss';
import classNames from 'classnames';

export interface InsightDocSummaryProps {
    files: FileInsightType[];
    emails?: EmailInsightType[];
    meetingId: string;
    colorScheme: ColorScheme;
    iconClass?: string;
    compactDocClass?: string;
    entrySource: InsightsDataFetchSource;
    onSeeMoreCountClicked?: (ev?: React.MouseEvent<HTMLElement>) => void;
}

export default observer(function InsightDocSummary(props: InsightDocSummaryProps) {
    const { files, emails, colorScheme, iconClass, entrySource } = props;
    if (files.length + (emails || []).length === 0) {
        return null;
    }

    const icon = getInsightDocRowIcon(files, colorScheme, iconClass);
    const noMargin = entrySource === InsightsDataFetchSource.AgendaView;

    return (
        <div
            tabIndex={0}
            aria-label={loc(meetingInsightsSectionAriaLabel)}
            className={classNames(styles.insightsSection, noMargin && styles.noMargin)}>
            {icon}
            <DocSummaryRow {...props} />
        </div>
    );
});

const DocSummaryRow = observer(function renderDocSummaryRow(props: InsightDocSummaryProps) {
    const {
        files,
        emails,
        meetingId,
        compactDocClass,
        entrySource,
        onSeeMoreCountClicked,
        colorScheme,
    } = props;

    const seeMoreCount = files.length + (emails || []).length - 1;
    return (
        <>
            <CompactDoc
                type="Document"
                insight={files[0]}
                meetingId={meetingId}
                compactDocClass={compactDocClass}
                entrySource={entrySource}
            />
            {seeMoreCount > 0 && (
                <MoreInsights
                    seeMoreCount={seeMoreCount}
                    onSeeMoreCountClicked={onSeeMoreCountClicked}
                    colorScheme={colorScheme}
                />
            )}
        </>
    );
});

interface MoreInsightsProps {
    seeMoreCount: number;
    colorScheme: ColorScheme;
    onSeeMoreCountClicked?: (ev?: React.MouseEvent<HTMLElement>) => void;
}

const MoreInsights = observer(function MoreInsights(props: MoreInsightsProps) {
    const { onSeeMoreCountClicked, colorScheme, seeMoreCount } = props;

    const text = `+${seeMoreCount}`;

    if (!onSeeMoreCountClicked) {
        return <span className={styles.seeMoreCount}>{text}</span>;
    }

    const buttonStyles = getShowMoreButtonStyles(colorScheme);
    return (
        <ActionButton
            text={text}
            ariaLabel={loc(showAllInsightsAriaLabel)}
            onClick={onSeeMoreCountClicked}
            styles={buttonStyles}
            className={styles.seeMoreCount}
        />
    );
});

function getShowMoreButtonStyles(colorScheme: ColorScheme): IButtonStyles {
    const palette = getPalette();
    const buttonColor = colorScheme?.accessibleText?.msColorWhiteBackground || palette.themePrimary;
    const buttonHoverColor = colorScheme ? colorScheme.calDarker : palette.themeDarker;

    return {
        root: {
            color: buttonColor,
            border: 0,
            padding: 0,
            margin: 0,
        },
        rootHovered: {
            color: buttonHoverColor,
        },
        rootPressed: {
            color: buttonHoverColor,
        },
        label: {
            margin: 0,
        },
    };
}
